import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import toast from 'react-hot-toast';
import { useTranslation, Trans } from 'react-i18next';

import {
  CheckCircle as CheckCircleIcon,
  AddCircle as AddCircleIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  ArrowRight as ArrowRightIcon,
  ArrowLeft as ArrowLeftIcon,
  LocalOffer as LocalOfferIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material/';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import '../../../assets/css/buttons.css';

// constants
const CLOSE_BEFORE_MS = 25_000;
const GAMES_PER_PAGE = 16;
const CARD_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30];

// color used to flag games with no card-price options configured
const NO_PRICE_COLOR = '#fb7185'; // rose

// Card-price options from the game's config.priceCard, falling back to the legacy flat priceCard
const getGamePrices = (g) => {
  const raw = g?.config?.priceCard;
  const list = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
  const nums = list.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  if (!nums.length && g?.priceCard != null && Number(g.priceCard) > 0) {
    nums.push(Number(g.priceCard));
  }
  return Array.from(new Set(nums)).sort((a, b) => a - b);
};

const timeStringToTodayDate = (t) => {
  const [h, m, s = 0] = String(t ?? '0:0:0')
    .split(':')
    .map(Number);
  const d = new Date();
  d.setHours(h || 0, m || 0, s || 0, 0);
  return d;
};

function PaymentScreen() {
  const { t } = useTranslation();
  const {
    updateIsLoadingScreen,
    updateIsSellerOpen,
    updateIsPapirOpen,
    hall,
    session,
    userInfo,
    parentInfo,
    updateTransactions,
    operator,
    printer,
    updateShowUserInfo,
    updateShowUserInfoSibling,
    updateShowUserInfoOK,
    updateShowUserInfoWG,
    updateShowUserInfoSiblingOK,
    transactions,
    closeTime,
  } = useContext(AppContext);

  const { socket } = useContext(SocketContext);

  // state
  const [rawGames, setRawGames] = useState([]);
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const [nextGame, setNextGame] = useState(null);
  const [gamesPage, setGamesPage] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [ticketsList, setTicketList] = useState([]);

  const [price, setPrice] = useState(0);
  const [cards, setCards] = useState(0);

  const [isRequesting, setIsRequesting] = useState(false);
  const [isReloadNeeded, setIsReloadNeeded] = useState(false);

  useEffect(() => {
    updateShowUserInfo(false);
    updateShowUserInfoSibling(false);
    updateShowUserInfoOK(false);
    updateShowUserInfoWG(false);
    updateShowUserInfoSiblingOK(false);
  }, []);

  // current time (ticks every second)
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // freeze the UI "clock" while a request is in-flight
  const [frozenAt, setFrozenAt] = useState(null);
  const timeBase = frozenAt ?? now;

  useEffect(() => {
    if (isRequesting) {
      setFrozenAt((prev) => prev ?? now);
    } else {
      setFrozenAt(null);
    }
  }, [isRequesting, now]);

  /* if a sale is in-flight and one of the selected games crosses cutoff, flag a reload */
  useEffect(() => {
    if (!isRequesting) return;
    const ids = selectedGameIdsRef.current;
    if (!ids || !ids.length) return;
    const someClosed = openGames
      .filter((g) => ids.includes(g.gameID))
      .some((g) => now >= cutOffTime(g));
    if (someClosed) {
      setIsReloadNeeded(true);
    }
  }, [isRequesting, now]);

  useEffect(() => {
    if (!isRequesting && isReloadNeeded) {
      handleComeBackToMainScreen();
      setIsReloadNeeded(false);
    }
  }, [isRequesting, isReloadNeeded]);

  // refs
  const updateTransactionsRef = useRef(updateTransactions);
  useEffect(() => {
    updateTransactionsRef.current = updateTransactions;
  }, [updateTransactions]);

  const operatorRef = useRef(operator);
  useEffect(() => {
    operatorRef.current = operator;
  }, [operator]);

  const hallRef = useRef(hall);
  useEffect(() => {
    hallRef.current = hall;
  }, [hall]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const parentInfoRef = useRef(parentInfo);
  useEffect(() => {
    parentInfoRef.current = parentInfo;
  }, [parentInfo]);

  const nextGameIdRef = useRef(nextGame);
  useEffect(() => {
    nextGameIdRef.current = nextGame;
  }, [nextGame]);

  const selectedGameIdsRef = useRef(selectedGameIds);
  useEffect(() => {
    selectedGameIdsRef.current = selectedGameIds;
  }, [selectedGameIds]);

  const ticketsListRef = useRef(ticketsList);
  useEffect(() => {
    ticketsListRef.current = ticketsList;
  }, [ticketsList]);

  const printerRef = useRef(printer);
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);

  const cardsRef = useRef(cards);
  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);

  const priceRef = useRef(price);
  useEffect(() => {
    priceRef.current = price;
  }, [price]);

  // batch sale queue: games are sold one-by-one (each its own transaction)
  const saleQueueRef = useRef([]);
  const pendingActionRef = useRef(null); // 'continue' | 'close'

  // derived data
  const cutOffTime = (g) => {
    const base = g.dateBuyEnd
      ? timeStringToTodayDate(g.dateBuyEnd)
      : timeStringToTodayDate(g.dateStart);
    const start = timeStringToTodayDate(g.dateStart);
    const explicitOrDefault = g.dateBuyEnd
      ? base
      : new Date(start.getTime() - CLOSE_BEFORE_MS);
    return Math.min(
      explicitOrDefault.getTime(),
      start.getTime() - CLOSE_BEFORE_MS
    );
  };

  const padTime = (t) => {
    if (t == null || t === '') return t;
    const [h = '0', m = '00'] = String(t).trim().split(':');
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  };

  const openGames = useMemo(() => {
    const normalized = rawGames.map((g) => ({
      ...g,
      dateStart: padTime(g.dateStart),
      dateEnd: padTime(g.dateEnd),
    }));

    const closeMs = closeTime
      ? timeStringToTodayDate(closeTime).getTime()
      : null;

    return normalized.filter((g) => {
      if (cutOffTime(g) <= timeBase) return false;
      if (closeMs !== null) {
        const startMs = timeStringToTodayDate(g.dateStart).getTime();
        if (startMs >= closeMs) return false;
      }
      return true;
    });
  }, [rawGames, timeBase, closeTime]);

  const sortedOpenGames = useMemo(() => {
    return [...openGames].sort(
      (a, b) =>
        a.dateStart.localeCompare(b.dateStart) ||
        a.gameID - b.gameID ||
        a.name.localeCompare(b.name)
    );
  }, [openGames]);

  // union of every open game's available card prices (the selectable options)
  const priceOptions = useMemo(() => {
    const all = new Set();
    openGames.forEach((g) => getGamePrices(g).forEach((p) => all.add(p)));
    return Array.from(all).sort((a, b) => a - b);
  }, [openGames]);

  // a game is playable at the chosen price (or when no price is chosen yet)
  const isGamePlayable = (g) => {
    const prices = getGamePrices(g);
    if (!prices.length) return false;
    return price === 0 || prices.includes(price);
  };

  const totalPages = Math.max(
    1,
    Math.ceil(sortedOpenGames.length / GAMES_PER_PAGE)
  );

  const paginatedGames = useMemo(() => {
    const start = (gamesPage - 1) * GAMES_PER_PAGE;
    return sortedOpenGames.slice(start, start + GAMES_PER_PAGE);
  }, [sortedOpenGames, gamesPage]);

  useEffect(() => {
    setGamesPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const nextCutOff = useMemo(() => {
    const future = openGames.map(cutOffTime).sort((a, b) => a - b);
    return future[0] || null;
  }, [openGames]);

  const mmss = nextCutOff
    ? new Date(Math.max(0, nextCutOff - timeBase))
        .toISOString()
        .substring(14, 19)
    : '--:--';

  // Drop any selected game once it closes or can no longer be played at the chosen price
  useEffect(() => {
    setSelectedGameIds((prev) =>
      prev.filter((id) => {
        const g = openGames.find((x) => x.gameID === id);
        return g ? isGamePlayable(g) : false;
      })
    );
  }, [openGames, price]);

  useEffect(() => {
    if (openGames && openGames.length) {
      setNextGame(openGames[0].gameID);
    }
  }, [openGames]);

  // selection summary
  const selectedGames = openGames.filter((g) =>
    selectedGameIds.includes(g.gameID)
  );
  const selectionTotal = selectedGames.length * cards * price;
  const selectedGamesLabel = [...selectedGameIds]
    .sort((a, b) => a - b)
    .join(', ');
  const canConfirm = selectedGameIds.length > 0 && cards > 0 && price > 0;

  // handlers
  const handleComeBackToMainScreen = () => {
    if (ticketsListRef.current.length > 0) {
      socket.emit('printPapirReceipt', {
        printer: printerRef.current,
        ticketsList: ticketsListRef.current,
        bingoName: hallRef.current,
      });
      updateIsLoadingScreen(true);
      return;
    }
    setSelectedGameIds([]);
    setPrice(0);
    setCards(0);
    setTicketList([]);
    ticketsListRef.current = [];
    updateIsPapirOpen(false);
    updateIsSellerOpen(false);
  };

  const emitTask = (infoToEmit) => {
    updateIsLoadingScreen(true);
    setIsRequesting(true);
    console.log('Emitting newSalePapir with data:', infoToEmit);
    socket.emit('newSalePapir', infoToEmit);
  };

  const handleCardsClick = (val) => {
    setCards(val);
  };

  const handlePriceClick = (val) => {
    setPrice((prev) => (prev === val ? 0 : val));
  };

  // toggle a game in/out of the multi-selection (playable games only)
  const toggleGame = (g) => {
    if (!isGamePlayable(g)) return;
    setSelectedGameIds((prev) =>
      prev.includes(g.gameID)
        ? prev.filter((id) => id !== g.gameID)
        : [...prev, g.gameID]
    );
  };

  // ask for confirmation before selling the current selection
  const requestConfirm = (action) => {
    if (
      selectedGameIdsRef.current.length === 0 ||
      cardsRef.current === 0 ||
      priceRef.current === 0
    ) {
      toast.error(t('sales.betAndCardsMustBeSelected'));
      return;
    }
    pendingActionRef.current = action; // 'continue' | 'close'
    setShowConfirm(true);
  };

  const handleModalCancel = () => {
    setShowConfirm(false);
    pendingActionRef.current = null;
  };

  const buildInfoToEmit = (item) => ({
    userID:
      parentInfoRef.current && parentInfoRef.current.id
        ? parentInfoRef.current.id
        : userInfoRef.current && userInfoRef.current.id
          ? userInfoRef.current.id
          : 0,
    user7Pincode:
      parentInfoRef.current && parentInfoRef.current['7 digit PIN']
        ? parentInfoRef.current['7 digit PIN']
        : userInfoRef.current && userInfoRef.current['7 digit PIN']
          ? userInfoRef.current['7 digit PIN']
          : 'Gjest',
    username: parentInfoRef.current
      ? parentInfoRef.current.username
      : userInfoRef.current.username,
    bingoName: hallRef.current,
    sessionID: sessionRef.current,
    gameID: item.gameID,
    isAnon: userInfoRef.current.username === 'Gjest' ? true : false,
    operator: operatorRef.current,
    name: item.name,
    priceCard: priceRef.current,
    cards: item.cards,
    printer: printerRef.current,
  });

  // emit the next queued game, or finish the batch when the queue drains
  const processNextSale = () => {
    const queue = saleQueueRef.current;
    if (!queue.length) {
      finishBatch();
      return;
    }
    const item = queue.shift();
    emitTask(buildInfoToEmit(item));
  };

  const finishBatch = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    saleQueueRef.current = [];
    setSelectedGameIds([]);
    if (action === 'close') {
      // Keep isRequesting up through receipt print and close so "Ferdig" never flashes in between
      handleComeBackToMainScreen();
    } else {
      setIsRequesting(false);
      updateIsLoadingScreen(false);
    }
  };

  // user confirmed in the modal: build the queue and start selling game-by-game
  const handleModalConfirm = () => {
    setShowConfirm(false);
    const ids = selectedGameIdsRef.current;
    const qty = cardsRef.current;
    const queue = openGames
      .filter((g) => ids.includes(g.gameID))
      .map((g) => ({
        gameID: g.gameID,
        name: g.name,
        cards: qty,
      }));
    if (!queue.length) {
      pendingActionRef.current = null;
      return;
    }
    saleQueueRef.current = queue;
    processNextSale();
  };

  // addCards (keeps ticketsListRef in sync synchronously)
  function addCards(newCards) {
    const batch = Array.isArray(newCards) ? newCards : [newCards];
    const keyFor = (gameID, priceCard) => `${gameID}::${priceCard ?? 'null'}`;

    const byPair = new Map(
      ticketsListRef.current.map((g) => [
        keyFor(g.gameID, g.priceCard),
        { ...g, tickets: [...g.tickets] },
      ])
    );

    for (const item of batch) {
      const { gameID, cardNumber, cardPrice } = item;
      if (gameID == null || cardNumber == null) continue;

      const k = keyFor(gameID, cardPrice);
      const entry = byPair.get(k) || {
        gameID,
        tickets: [],
        priceCard: cardPrice ?? undefined,
      };

      if (!entry.tickets.includes(cardNumber)) {
        entry.tickets.push(cardNumber);
      }

      byPair.set(k, entry);
    }

    const next = Array.from(byPair.values())
      .map((g) => ({
        ...g,
        tickets: g.tickets.slice().sort((a, b) => a - b),
      }))
      .sort(
        (a, b) => a.gameID - b.gameID || (a.priceCard ?? 0) - (b.priceCard ?? 0)
      );

    ticketsListRef.current = next;
    setTicketList(next);
  }

  // socket
  useEffect(() => {
    socket.emit('getGamePlanPW', '');

    const handleGetGamePlan = (data) => {
      updateIsLoadingScreen(false);
      if (!data) {
        toast.error(t('error.error'));
        return;
      }
      setRawGames(data);
    };

    const handleNewSalePapir = async (data) => {
      console.log('Received newSalePapir response:', data);
      if (!data) {
        toast.error(t('error.error'));
        // abort the rest of the batch
        saleQueueRef.current = [];
        pendingActionRef.current = null;
        updateIsLoadingScreen(false);
        setIsRequesting(false);
        return;
      }

      addCards(data.cards);

      // one transaction line per game, derived from the returned cards
      const sold = Array.isArray(data.cards) ? data.cards : [];
      const priceCard = data.priceCard;
      const count = sold.length;
      updateTransactionsRef.current({
        name:
          'Ny Papir Billett' +
          ' - ' +
          'CH (' +
          priceCard +
          ' KR x' +
          count +
          ')',
        amount: parseInt(priceCard * count),
        type: 'transaction',
      });

      // continue with the next queued game (or finish the batch)
      processNextSale();
    };

    const handlePrintPapirReceipt = (data) => {
      updateIsLoadingScreen(false);
      setSelectedGameIds([]);
      setPrice(0);
      setCards(0);
      setTicketList([]);
      ticketsListRef.current = [];
      updateIsPapirOpen(false);
      updateIsSellerOpen(false);
      setIsRequesting(false);
    };

    socket.on('getGamePlanPW', handleGetGamePlan);
    socket.on('newSalePapir', handleNewSalePapir);
    socket.on('printPapirReceipt', handlePrintPapirReceipt);
    return () => {
      socket.off('getGamePlanPW', handleGetGamePlan);
      socket.off('newSalePapir', handleNewSalePapir);
      socket.off('printPapirReceipt', handlePrintPapirReceipt);
    };
  }, [socket]);

  // Drag-to-scroll for the games grid; pointer events unify mouse and touch (and VNC)
  const gamesListRef = useRef(null);
  const gamesDrag = useRef({ active: false, startY: 0, startScrollTop: 0 });

  const handleGamesPointerDown = (event) => {
    const el = gamesListRef.current;
    if (!el) return;
    gamesDrag.current = {
      active: true,
      startY: event.clientY,
      startScrollTop: el.scrollTop,
    };
  };

  const handleGamesPointerMove = (event) => {
    const el = gamesListRef.current;
    if (!el || !gamesDrag.current.active) return;
    const delta = event.clientY - gamesDrag.current.startY;
    el.scrollTop = gamesDrag.current.startScrollTop - delta;
  };

  const endGamesDrag = () => {
    gamesDrag.current.active = false;
  };

  // render
  return (
    <Grid
      item
      xs={12}
      sx={{
        height: '100vh',
        p: 3,
        borderRadius: 4,
        background: 'transparent',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* title */}
      <Typography
        variant="h4"
        align="left"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <PrintIcon sx={{ fontSize: 40 }} />
        {t('sales.paper')}
      </Typography>

      {/* countdown */}
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography sx={{ fontSize: '1.2em' }}>
            {t('sales.countdownToNextGame1')}
            <strong>{nextGameIdRef.current}</strong>{' '}
            {t('sales.countdownToNextGame2')}
            <strong>{mmss}</strong>
          </Typography>
        </Grid>
      </Grid>

      {/* card-price selector (union of every open game's config.priceCard) */}
      <Box sx={{ mt: 2 }}>
        <Typography
          sx={{
            color: '#FFC727',
            fontWeight: 900,
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 0.75,
          }}
        >
          {t('kiosk.priceCardLabel')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          {priceOptions.length ? (
            priceOptions.map((n) => {
              const active = price === n;
              return (
                <Button
                  key={n}
                  onClick={() => handlePriceClick(n)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 56,
                    borderRadius: '12px',
                    fontWeight: 900,
                    fontSize: '20px',
                    textTransform: 'none',
                    color: active ? '#000' : '#fff',
                    bgcolor: active ? '#e3a406' : 'rgba(255,255,255,0.08)',
                    border: active
                      ? '1px solid #e3a406'
                      : '1px solid rgba(255,255,255,0.14)',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: active ? '#f0b41f' : 'rgba(255,255,255,0.16)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {n} kr
                </Button>
              );
            })
          ) : (
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.55)',
                fontWeight: 700,
                fontSize: '16px',
                py: 1.5,
              }}
            >
              {t('kiosk.noGamesAvailable')}
            </Typography>
          )}
        </Box>
      </Box>

      {/* sticky card-quantity selector (full width) */}
      <Box sx={{ mt: 2 }}>
        <Typography
          sx={{
            color: '#FFC727',
            fontWeight: 900,
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 0.75,
          }}
        >
          {t('sales.cards')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          {CARD_OPTIONS.map((n) => {
            const active = cards === n;
            return (
              <Button
                key={n}
                onClick={() => handleCardsClick(n)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 56,
                  borderRadius: '12px',
                  fontWeight: 900,
                  fontSize: '20px',
                  textTransform: 'none',
                  color: active ? '#000' : '#fff',
                  bgcolor: active ? '#FFC727' : 'rgba(255,255,255,0.08)',
                  border: active
                    ? '1px solid #FFC727'
                    : '1px solid rgba(255,255,255,0.14)',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: active ? '#FFD54F' : 'rgba(255,255,255,0.16)',
                    boxShadow: 'none',
                  },
                }}
              >
                x{n}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* games grid (multi-select) */}
      <Box
        ref={gamesListRef}
        onPointerDown={handleGamesPointerDown}
        onPointerMove={handleGamesPointerMove}
        onPointerUp={endGamesDrag}
        onPointerLeave={endGamesDrag}
        onPointerCancel={endGamesDrag}
        sx={{
          mt: 2,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          scrollbarWidth: 'none',
          '-ms-overflow-style': 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'safe center',
        }}
      >
        <Grid container spacing={1.5} justifyContent="flex-start">
          {paginatedGames.map((g) => {
            const selected = selectedGameIds.includes(g.gameID);
            const formattedName = g.name
              ? g.name.charAt(0).toUpperCase() + g.name.slice(1).toLowerCase()
              : '';
            const prices = getGamePrices(g);
            const sellable = prices.length > 0;
            const disabled = !isGamePlayable(g);
            const highlightColor = !sellable ? NO_PRICE_COLOR : null;
            return (
              <Grid
                item
                xs={6}
                sm={4}
                md={3}
                key={`${g.gameID}-${g.dateStart}-${g.name}`}
              >
                <Button
                  onClick={() => toggleGame(g)}
                  disabled={disabled}
                  variant={selected ? 'contained' : 'outlined'}
                  sx={{
                    width: '100%',
                    minHeight: 84,
                    borderColor:
                      !selected && highlightColor
                        ? highlightColor
                        : 'rgba(255,255,255,0.22)',
                    color: selected ? 'black' : '#fff',
                    bgcolor: selected ? 'primary.main' : 'transparent',
                    textAlign: 'left',
                    px: 1.5,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'stretch',
                    justifyContent: 'space-between',
                    gap: 1.2,
                    borderRadius: '10px',
                    boxShadow:
                      !selected && highlightColor
                        ? `0 0 0 1px ${highlightColor}55, 0 0 16px ${highlightColor}55`
                        : 'none',
                    '&:hover': {
                      borderColor: highlightColor || '#fff',
                      bgcolor: selected
                        ? 'primary.dark'
                        : 'rgba(255,255,255,0.08)',
                      boxShadow:
                        !selected && highlightColor
                          ? `0 0 0 1.5px ${highlightColor}, 0 0 22px ${highlightColor}88`
                          : 'none',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.3,
                      color: 'rgba(255,255,255,0.5)',
                      borderColor: 'rgba(255,255,255,0.14)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      minWidth: 0,
                      flex: 1,
                      gap: 0.3,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{ fontWeight: 900, fontSize: '20px' }}
                    >
                      {t('kiosk.gameLabel')} {g.gameID}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        opacity: 0.9,
                        fontWeight: 700,
                        fontSize: '15px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                    >
                      {formattedName}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color:
                          !selected && highlightColor
                            ? highlightColor
                            : 'inherit',
                      }}
                    >
                      {sellable ? (
                        <LocalOfferIcon
                          sx={{
                            fontSize: 14,
                            color: selected ? 'inherit' : '#FFC727',
                          }}
                        />
                      ) : (
                        <ErrorOutlineIcon
                          sx={{
                            fontSize: 14,
                            color: selected ? 'inherit' : NO_PRICE_COLOR,
                          }}
                        />
                      )}
                      <Typography
                        component="span"
                        sx={{
                          opacity: 0.92,
                          fontWeight: 800,
                          fontSize: '12px',
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {sellable ? `${prices.join(' / ')} kr` : '-'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      minWidth: 78,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderLeft: '1px solid rgba(255,255,255,0.18)',
                      pl: 1.2,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 900,
                        fontSize: '22px',
                        lineHeight: 1,
                      }}
                    >
                      {g.dateStart}
                    </Typography>
                  </Box>
                </Button>
              </Grid>
            );
          })}

          {!paginatedGames.length && (
            <Grid item xs={12}>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.72)',
                  textAlign: 'center',
                  py: 2,
                }}
              >
                {t('kiosk.noGamesAvailable')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ mt: 1.5, gap: 2 }}
      >
        <Button
          variant="contained"
          onClick={() => setGamesPage((p) => Math.max(1, p - 1))}
          disabled={gamesPage === 1}
          sx={{
            minWidth: '60px',
            height: '50px',
            borderRadius: '12px',
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: 'rgba(255,255,255,0.85)',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.16)',
              boxShadow: 'none',
            },
            '&:disabled': { opacity: 0.3 },
          }}
        >
          <ArrowLeftIcon sx={{ fontSize: '30px' }} />
        </Button>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.85)',
            fontWeight: 800,
            fontSize: '18px',
            minWidth: '90px',
            textAlign: 'center',
          }}
        >
          {t('kiosk.pageIndicator', {
            current: gamesPage,
            total: totalPages,
          })}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setGamesPage((p) => Math.min(totalPages, p + 1))}
          disabled={gamesPage === totalPages}
          sx={{
            minWidth: '60px',
            height: '50px',
            borderRadius: '12px',
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: 'rgba(255,255,255,0.85)',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.16)',
              boxShadow: 'none',
            },
            '&:disabled': { opacity: 0.3 },
          }}
        >
          <ArrowRightIcon sx={{ fontSize: '30px' }} />
        </Button>
      </Grid>

      {/* selection summary (full width) */}
      <Box
        sx={{
          mt: 1.5,
          width: '100%',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.14)',
          background: 'rgba(255,255,255,0.06)',
          px: 2,
          py: 1.5,
        }}
      >
        {canConfirm ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              width: '100%',
              minWidth: 0,
            }}
          >
            {/* per-game segments (scroll horizontally if they overflow;
                the total stays pinned and visible) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flex: '1 1 auto',
                minWidth: 0,
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                touchAction: 'pan-x',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                '-ms-overflow-style': 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                // fade the right edge to hint there's more to scroll →
                maskImage:
                  'linear-gradient(to right, #000 calc(100% - 28px), transparent 100%)',
                WebkitMaskImage:
                  'linear-gradient(to right, #000 calc(100% - 28px), transparent 100%)',
                fontSize: '18px',
                fontWeight: 700,
                color: '#fff',
              }}
            >
              {[...selectedGames]
                .sort((a, b) => a.gameID - b.gameID)
                .map((g, i) => {
                  const lineTotal = cards * price;
                  return (
                    <Box
                      component="span"
                      key={g.gameID}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      {i > 0 && (
                        <Box
                          component="span"
                          sx={{ color: 'rgba(255,255,255,0.3)' }}
                        >
                          ·
                        </Box>
                      )}
                      <Box component="span" sx={{ fontWeight: 900 }}>
                        {t('kiosk.gameLabel')} {g.gameID}
                      </Box>
                      <Box
                        component="span"
                        sx={{ opacity: 0.7, fontWeight: 600, fontSize: '15px' }}
                      >
                        {cards}×{price} = {lineTotal} kr
                      </Box>
                    </Box>
                  );
                })}
            </Box>

            {/* grand total — pinned to the right, button-yellow */}
            <Box
              component="span"
              sx={{
                flex: '0 0 auto',
                ml: 'auto',
                pl: 2,
                fontWeight: 900,
                fontSize: '22px',
                color: '#FFC727',
              }}
            >
              {selectionTotal} kr
            </Box>
          </Box>
        ) : (
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '20px',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.55)',
            }}
          >
            {t('sales.selectGameAndCards')}
          </Typography>
        )}
      </Box>

      {/* action buttons (bottom) — same aesthetic as the comeback button */}
      <Box
        sx={{
          height: '120px',
          mt: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <button
          className="btn-custom-postpone btn-label-wide"
          onClick={() => {
            if (ticketsList.length === 0) handleComeBackToMainScreen();
          }}
          disabled={ticketsList.length > 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
            opacity: ticketsList.length > 0 ? 0.4 : 1,
            cursor: ticketsList.length > 0 ? 'not-allowed' : 'pointer',
          }}
        >
          <ArrowBackIcon style={{ fontSize: '20px' }} />
          <span>{t('sales.backToMainScreen')}</span>
        </button>

        <button
          className="btn-custom-yes-and-stay btn-label-wide"
          onClick={() => requestConfirm('continue')}
          disabled={!canConfirm}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
            opacity: canConfirm ? 1 : 0.4,
            cursor: canConfirm ? 'pointer' : 'not-allowed',
          }}
        >
          <AddCircleIcon style={{ fontSize: '20px' }} />
          <span>{t('sales.confirmAndContinue')}</span>
        </button>

        <button
          className="btn-custom-yes btn-label-wide"
          onClick={() => requestConfirm('close')}
          disabled={!canConfirm}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
            opacity: canConfirm ? 1 : 0.4,
            cursor: canConfirm ? 'pointer' : 'not-allowed',
          }}
        >
          <CheckCircleIcon style={{ fontSize: '20px' }} />
          <span>{t('sales.confirmAndClose')}</span>
        </button>

        {ticketsList.length > 0 && !canConfirm && !isRequesting && (
          <button
            className="btn-custom-yes btn-label-wide"
            onClick={() => handleComeBackToMainScreen()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
            }}
          >
            <PrintIcon style={{ fontSize: '20px' }} />
            <span>{t('sales.done')}</span>
          </button>
        )}
      </Box>

      {/* confirmation modal */}
      {showConfirm && (
        <div id="react-confirm-alert">
          <div className="react-confirm-alert-overlay">
            <div className="react-confirm-alert">
              <div
                className="react-confirm-alert-body"
                style={{ width: '1000px' }}
              >
                <h1>{t('sales.confirmKingoSaleTitle')}</h1>
                <p style={{ fontSize: '22px', marginTop: '20px' }}>
                  <Trans
                    i18nKey="sales.confirmPapirBatchWithPrice"
                    values={{
                      cards: cards,
                      price: price,
                      games: selectedGamesLabel,
                      total: selectionTotal,
                    }}
                    components={{
                      hl: (
                        <strong
                          style={{ color: '#42a5f5', fontWeight: 'bold' }}
                        />
                      ),
                    }}
                  />
                </p>
                <div
                  className="react-confirm-alert-button-group"
                  style={{ marginTop: '55px' }}
                >
                  <button
                    className="btn-custom-yes"
                    onClick={() => handleModalConfirm()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.confirm')}
                  </button>
                  <button
                    className="btn-custom-no"
                    onClick={() => handleModalCancel()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CancelIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Grid>
  );
}

export default PaymentScreen;
